import { __assign } from 'tslib';
import React, { forwardRef, useState, useCallback, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { useMatchMedia } from '@alfalab/core-components-mq/esm';
import { Scrollbar } from '@alfalab/core-components-scrollbar/esm';
import { isClient } from '@alfalab/core-components-shared/esm';
import { SIZE_TO_CLASSNAME_MAP, DEFAULT_VISIBLE_OPTIONS } from '../../consts.js';
import { useVisibleOptions, isGroup } from '../../utils.js';
import { Optgroup } from '../optgroup/Component.js';

var styles = {"optionsList":"select__optionsList_dajj4","optionsListHeader":"select__optionsListHeader_dajj4","headerHighlighted":"select__headerHighlighted_dajj4","optionsListFooter":"select__optionsListFooter_dajj4","withBorder":"select__withBorder_dajj4","scrollable":"select__scrollable_dajj4","-webkit-mask":"select__mask_dajj4","mask":"select__mask_dajj4","emptyPlaceholder":"select__emptyPlaceholder_dajj4","size-64":"select__size-64_dajj4","size-72":"select__size-72_dajj4"};
require('./index.css')

var createCounter = function () {
    var count = 0;
    // eslint-disable-next-line no-plusplus
    return function () { return count++; };
};
var OptionsList = forwardRef(function (_a, ref) {
    var _b, _c;
    var _d = _a.size, size = _d === void 0 ? 48 : _d, className = _a.className, optionGroupClassName = _a.optionGroupClassName, footerClassName = _a.footerClassName, scrollbarClassName = _a.scrollbarClassName, Option = _a.Option, getOptionProps = _a.getOptionProps, _e = _a.groupOptionProps, groupOptionProps = _e === void 0 ? {} : _e, _f = _a.options, options = _f === void 0 ? [] : _f, _g = _a.Optgroup, Optgroup$1 = _g === void 0 ? Optgroup : _g, dataTestId = _a.dataTestId, emptyPlaceholder = _a.emptyPlaceholder, _h = _a.visibleOptions, visibleOptions = _h === void 0 ? DEFAULT_VISIBLE_OPTIONS : _h, onScroll = _a.onScroll, open = _a.open, header = _a.header, footer = _a.footer, _j = _a.showFooter, showFooter = _j === void 0 ? true : _j, optionsListWidth = _a.optionsListWidth, nativeScrollbarProp = _a.nativeScrollbar, _k = _a.flatOptions, flatOptions = _k === void 0 ? [] : _k, setHighlightedIndex = _a.setHighlightedIndex, selectedItems = _a.selectedItems, search = _a.search, setSelectedItems = _a.setSelectedItems, multiple = _a.multiple, _l = _a.limitDynamicOptionGroupSize, limitDynamicOptionGroupSize = _l === void 0 ? false : _l;
    var _m = useState(true), scrollTop = _m[0], setScrollTop = _m[1];
    var _o = useState(false), scrollBottom = _o[0], setScrollBottom = _o[1];
    var query = '(max-width: 1023px)';
    var nativeScrollbar = useMatchMedia(query, function () {
        return isClient() ? window.matchMedia(query).matches : true;
    })[0];
    nativeScrollbar = Boolean(nativeScrollbarProp !== null && nativeScrollbarProp !== void 0 ? nativeScrollbarProp : nativeScrollbar);
    var handleScroll = useCallback(function (event) {
        var scrolledToHeader = event.currentTarget.scrollTop <= 0;
        var scrolledToFooter = event.currentTarget.scrollHeight - event.currentTarget.offsetHeight <=
            event.currentTarget.scrollTop;
        setScrollTop(scrolledToHeader);
        setScrollBottom(scrolledToFooter);
        onScroll === null || onScroll === void 0 ? void 0 : onScroll(event);
    }, [onScroll]);
    var renderOption = function (option, index) { return (React.createElement(Option, __assign({ key: option.key }, getOptionProps(option, index)))); };
    var listRef = useRef(null);
    var scrollbarRef = useRef(null);
    var counter = createCounter();
    var renderGroup = function (group) {
        var groupSelectedItems = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.filter(function (_a) {
            var selectedItemKey = _a.key;
            return group.options.some(function (option) { return option.key === selectedItemKey; });
        });
        var handleSelectedItems = function (items) {
            var _a;
            setSelectedItems(((_a = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.filter(function (_a) {
                var selectedItemKey = _a.key;
                return !group.options.some(function (option) { return option.key === selectedItemKey; });
            })) !== null && _a !== void 0 ? _a : []).concat(items));
        };
        return (React.createElement(Optgroup$1, __assign({ className: optionGroupClassName, label: group.label, key: group.label, size: size, options: group.options, selectedItems: groupSelectedItems, setSelectedItems: handleSelectedItems, search: search, multiple: multiple }, groupOptionProps), group.options.map(function (option) { return renderOption(option, counter()); })));
    };
    var actualOptionsCount = limitDynamicOptionGroupSize && options.length > 0;
    var measured = useVisibleOptions(__assign(__assign({}, (!nativeScrollbar && { styleTargetRef: scrollbarRef })), { visibleOptions: visibleOptions, listRef: listRef, open: open, options: options, actualOptionsCount: actualOptionsCount, size: actualOptionsCount
            ? (function () {
                switch (typeof size) {
                    case 'string':
                        throw new Error('OptionsList with `limitDynamicOptionGroupSize` enabled needs a `size` with number type');
                    default:
                        return size;
                }
            })()
            : undefined }));
    if (options.length === 0 && !emptyPlaceholder && !header && !footer) {
        return null;
    }
    var renderListItems = function () { return (React.createElement(React.Fragment, null,
        options.map(function (option) {
            return isGroup(option) ? renderGroup(option) : renderOption(option, counter());
        }),
        emptyPlaceholder && options.length === 0 && (React.createElement("div", { className: styles.emptyPlaceholder }, emptyPlaceholder)))); };
    var renderWithCustomScrollbar = function () {
        var _a;
        var scrollableNodeProps = {
            onScroll: handleScroll,
            'data-test-id': dataTestId,
            ref: ref,
        };
        return (React.createElement(Scrollbar, { className: cn(styles.scrollable, scrollbarClassName), ref: scrollbarRef, horizontalAutoStretch: optionsListWidth === 'content', scrollableNodeProps: scrollableNodeProps, contentNodeProps: { ref: listRef }, maskProps: {
                /*
                 * Для корректного подсчета высоты опций(иначе для optionsListWidth: 'field'
                 * высота опции всегда будет равна высоте одной строчки)
                 */
                className: cn((_a = {},
                    _a[styles.mask] = optionsListWidth === 'content' && !measured,
                    _a)),
            } }, renderListItems()));
    };
    var renderWithNativeScrollbar = function () { return (React.createElement("div", { className: cn(styles.scrollable, scrollbarClassName), ref: mergeRefs([listRef, ref]), onScroll: handleScroll }, renderListItems())); };
    var resetHighlightedIndex = function () { return setHighlightedIndex === null || setHighlightedIndex === void 0 ? void 0 : setHighlightedIndex(-1); };
    return (React.createElement("div", __assign({}, (nativeScrollbar && { 'data-test-id': dataTestId }), { className: cn(styles.optionsList, styles[SIZE_TO_CLASSNAME_MAP[size]], className) }),
        header && (React.createElement("div", { className: cn(styles.optionsListHeader, (_b = {},
                _b[styles.headerHighlighted] = !scrollTop,
                _b)), onMouseEnter: resetHighlightedIndex }, header)),
        nativeScrollbar ? renderWithNativeScrollbar() : renderWithCustomScrollbar(),
        showFooter && footer && (React.createElement("div", { onMouseEnter: resetHighlightedIndex, className: cn(styles.optionsListFooter, footerClassName, (_c = {},
                _c[styles.withBorder] = visibleOptions &&
                    flatOptions.length > visibleOptions &&
                    !scrollBottom,
                _c)) }, footer))));
});

export { OptionsList };
